import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  Paginated,
  PendingDemandDto,
  RequisitionDetailDto,
  RequisitionDto,
  RequisitionTransitionResultDto,
} from '@eombor/contracts';
import { notificationKeys } from '../../notification/model/notification.queries.js';
import { isStoppedStatus } from './requisition-status.js';
import { requesterOriginOf } from './requester-origin.js';
import {
  requisitionApi,
  type RequisitionQuery,
  type TransitionBody,
} from '../api/requisition.api.js';

/**
 * Talabnoma so'rovlari bitta joyda: kalitlar ham shu yerda turadi, shuning uchun
 * o'tishdan keyin `invalidateQueries({ queryKey: requisitionKeys.all })`
 * kartochkani ham, ro'yxatni ham, "mening navbatim" ni ham bir xil yangilaydi.
 */
export const requisitionKeys = {
  all: ['requisition'] as const,
  list: (query: RequisitionQuery) => ['requisition', 'list', query] as const,
  inbox: ['requisition', 'inbox'] as const,
  byId: (id: string | null) => ['requisition', 'card', id] as const,
  demand: (
    warehouseId: string | null,
    nomenclatureIds: readonly string[],
    excludeRequisitionId: string | null,
  ) =>
    [
      'requisition',
      'demand',
      warehouseId,
      [...nomenclatureIds].sort().join(','),
      excludeRequisitionId,
    ] as const,
};

/**
 * "Mening navbatim" o'zi yangilanadi.
 *
 * Talabnoma navbatga BOSHQA xodimning amalidan tushadi — sahifa ochiq turganda
 * hech qanday mahalliy hodisa bo'lmaydi. 60 soniya: smena ichida sezilarli
 * kechikish emas, serverga ham yuk emas (bildirishnoma sanog'i bilan bir xil).
 */
export const INBOX_REFETCH_MS = 60_000;

/**
 * OCHIQ TURGAN ekran o'zi yangilanadi.
 *
 * Talabnomaning holatini BOSHQA odam o'zgartiradi — rahbar tasdiqlaydi,
 * skladchi beradi — va ekranda hech qanday mahalliy hodisa bo'lmaydi.
 * Odam esa "tasdiqladimi?" deb sahifani qayta yuklab turadi.
 *
 * SOKET EMAS, so'rov. Soket uchun serverda alohida darvoza, tokenni soket
 * ustidan tekshirish va proksida alohida sozlama kerak bo'lardi — bularning
 * hammasi bitta sonni yangilash uchun. 20 soniya odam sezmaydigan kechikish,
 * server uchun esa arzimas yuk: javob kichkina va faqat OCHIQ sahifa
 * so'raydi (fon ilovada so'rov ketmaydi).
 */
export const LIVE_REFETCH_MS = 20_000;

export const useRequisitions = (
  query: RequisitionQuery = {},
): UseQueryResult<Paginated<RequisitionDto>> =>
  useQuery({
    queryKey: requisitionKeys.list(query),
    queryFn: () => requisitionApi.list(query),
    refetchInterval: LIVE_REFETCH_MS,
  });

export const useInbox = (): UseQueryResult<Paginated<RequisitionDto>> =>
  useQuery({
    queryKey: requisitionKeys.inbox,
    queryFn: () => requisitionApi.inbox(1, 50),
    refetchInterval: INBOX_REFETCH_MS,
  });

export const useRequisition = (id: string | null): UseQueryResult<RequisitionDetailDto> =>
  useQuery({
    queryKey: requisitionKeys.byId(id),
    queryFn: () => requisitionApi.byId(id ?? ''),
    enabled: id !== null,
    /* TUGAGAN hujjat so'ralmaydi: yopilgan yoki rad etilgan talabnomaning
       holati boshqa o'zgarmaydi va uni har 20 soniyada so'rash bekorga
       urinish bo'lardi. */
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status !== undefined && isStoppedStatus(status) ? false : LIVE_REFETCH_MS;
    },
  });

/**
 * BUYURTMACHINING ISMI ro'yxat kartochkalari uchun — `requesterUserId → ism`.
 *
 * Ro'yxat javobida ism YO'Q, faqat hujjatning o'zida (`requesterName`)
 * keladi. Xodimlar ro'yxati (`/users`) har kimga ochiq emas, hujjat esa
 * ro'yxatni ko'rgan har bir odamga ochiq — shuning uchun ism o'sha yerdan
 * olinadi.
 *
 * Ism HUJJATGA emas, ODAMGA bog'liq: har bir buyurtmachi uchun faqat
 * BITTA hujjat so'raladi (ellik kartochka — odatda bir necha so'rov).
 * Kalit kartochka ekrani bilan bir xil, demak hujjat keyin ochilganda u
 * keshdan chiqadi. Server ismni ro'yxatning o'zida qaytara boshlasa
 * (`model/requester-origin.ts`), bunday satr uchun so'rov ketmaydi.
 */
export const useRequesterNames = (
  rows: readonly RequisitionDto[],
): ReadonlyMap<string, string> => {
  const docByUser = new Map<string, string>();
  for (const row of rows) {
    if (requesterOriginOf(row).creator !== null) continue;
    if (!docByUser.has(row.requesterUserId)) docByUser.set(row.requesterUserId, row.id);
  }
  const pairs = [...docByUser].sort(([a], [b]) => a.localeCompare(b));

  const results = useQueries({
    queries: pairs.map(([, docId]) => ({
      queryKey: requisitionKeys.byId(docId),
      queryFn: () => requisitionApi.byId(docId),
      /* Ism kamdan-kam o'zgaradi — ro'yxat har 20 soniyada yangilanganda
         qayta so'rash shart emas. */
      staleTime: 300_000,
    })),
  });

  const names = new Map<string, string>();
  pairs.forEach(([userId], index) => {
    const name = results[index]?.data?.requesterName?.trim();
    if (name) names.set(userId, name);
  });
  return names;
};

/**
 * Idempotentlik kaliti (`idempotencyKeySchema`: kamida 8 belgi).
 *
 * `randomUUID` yo'q muhitlar ham bor (HTTPS bo'lmagan kontekst, eski jsdom) —
 * u yerda vaqt va tasodifdan yasaladi. Maqsad bitta: BIR urinishning takroriy
 * yuborilishini server tanib olsin.
 */
export const newIdempotencyKey = (): string =>
  typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

/**
 * Holatni o'zgartirish.
 *
 * Javobdagi kartochka darhol keshga yoziladi (`setQueryData`) — tugma
 * bosilgandan keyin ekran bir zumga eski holatda qolib ketmaydi. Qolgan
 * ro'yxatlar esa bekor qilinadi: o'tish navbatni ham, bildirishnomani ham
 * o'zgartiradi.
 */
export const useTransition = (
  id: string,
): UseMutationResult<RequisitionTransitionResultDto, unknown, TransitionBody> => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: TransitionBody) =>
      requisitionApi.transition(id, body, newIdempotencyKey()),
    onSuccess: (result) => {
      client.setQueryData<RequisitionDetailDto>(requisitionKeys.byId(id), {
        ...result.requisition,
        availableTransitions: result.availableTransitions,
      });
      void client.invalidateQueries({ queryKey: requisitionKeys.list({}), exact: false });
      void client.invalidateQueries({ queryKey: requisitionKeys.inbox });
      void client.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
};

/**
 * KUTILAYOTGAN TALAB — hali band qilinmagan, lekin so'ralgan miqdor.
 *
 * Ekranda mavjud son yonida turadi va aynan shu bo'shliqni yopadi: bron
 * faqat skladchi qabul qilganda qo'yiladi, undan oldin esa qoldiq umuman
 * kamaymaydi. Uch stansiya bir vaqtda o'sha bitta partiyani so'rasa,
 * ekran uchchalasiga ham "hammasi bor" deb turardi.
 *
 * Ombor bo'yicha JUFTLIK qaytadi (`warehouseId` + `nomenclatureId`), chunki
 * talabnoma aynan bitta omborga yoziladi va boshqa ombordagi talab bu
 * omborning qoldig'iga daxlsiz.
 */
export const usePendingDemand = (
  nomenclatureIds: readonly string[],
  warehouseId: string | null = null,
  /**
   * Shu talabnomaning O'ZI sanoqdan chiqariladi.
   *
   * Boshliq tasdiqlash oynasida "boshqa talabnomalarda qancha so'ralgan"
   * degan savolga javob ko'radi. O'zi ko'rib turgan hujjat ham sanalsa,
   * son o'z-o'zini hisobga olib, "yetmayapti" deb qo'rqitardi.
   */
  excludeRequisitionId: string | null = null,
): UseQueryResult<PendingDemandDto[]> =>
  useQuery({
    queryKey: requisitionKeys.demand(warehouseId, nomenclatureIds, excludeRequisitionId),
    queryFn: () =>
      requisitionApi.demand({
        ...(warehouseId === null ? {} : { warehouseId }),
        ...(excludeRequisitionId === null ? {} : { excludeRequisitionId }),
        nomenclatureIds,
      }),
    // Ro'yxat bo'sh bo'lsa so'rov ham ma'nosiz: javob har doim bo'sh bo'lardi.
    enabled: nomenclatureIds.length > 0,
    /* Talab BOSHQA odamlarning amalidan o'zgaradi va sahifada hech qanday
       mahalliy hodisa bo'lmaydi. Qoldiq bilan bir xil tezlikda eskiradi. */
    refetchInterval: LIVE_REFETCH_MS,
  });

/**
 * Talabni qidirish uchun qulay shakl: `warehouseId:nomenclatureId` → miqdor.
 *
 * Kartochka har ombor qatorida shu savolni beradi va ro'yxat bo'ylab
 * aylanish `O(n²)` bo'lib ketardi.
 */
export const demandMapOf = (rows: readonly PendingDemandDto[]): Map<string, number> =>
  new Map(rows.map((row) => [`${row.warehouseId}:${row.nomenclatureId}`, row.qty]));
